<template>
  <ToastError ref="ToastError" :text="passwordErrorText" />
  <ToastError
    ref="ToastSaveError"
    text="Произошла ошибка. Изменения не сохранены"
  />
  <ToastSuccess ref="ToastSaveSuccess" text="Изменения сохранены" />

  <ActionModal
    v-model="deleteModalVisible"
    title="Удалить аккаунт"
    subtitle="Аккаунт будет удалён без возможности восстановления"
    :actions="buttonModalDelete"
  />
  <ModalBlock
    v-model="changePasswordModalVisible"
    :model="changePasswordModalVisible"
    :headerModal="!isPasswordChanged"
  >
    <div :class="[style.modal, style.modalChange]" v-if="!isPasswordChanged">
      <div :class="style.header">
        <h3>Изменить пароль</h3>
        <span>Укажите новый пароль и подтвердите его.</span>
      </div>
      <div :class="style.inputs">
        <InputStyle
          size="M"
          :type="isShowPreviousPassword ? 'text' : 'password'"
          placeholder="Старый пароль"
          :isError="passwordsError.passwordOldError"
          v-model="passwords.passwordOld"
        >
          <template #actions>
            <ButtonIcon
              :icon="isShowPreviousPassword ? eyeOff : eyeOn"
              @click.stop.prevent="showPreviousPassword"
            />
          </template>
        </InputStyle>

        <InputStyle
          size="M"
          :type="isShowCurrentPassword ? 'text' : 'password'"
          placeholder="Новый пароль"
          :isError="passwordsError.passwordNewError"
          v-model="passwords.passwordNew"
        >
          <template #actions>
            <ButtonIcon
              :icon="isShowCurrentPassword ? eyeOff : eyeOn"
              @click.stop.prevent="showCurrentPassword"
            />
          </template>
        </InputStyle>

        <InputStyle
          size="M"
          :type="isShowCurrentPasswordAgain ? 'text' : 'password'"
          placeholder="Подтвердите пароль"
          :isError="passwordsError.passwordNewCheckError"
          v-model="passwords.passwordNewCheck"
        >
          <template #actions>
            <ButtonIcon
              :icon="isShowCurrentPasswordAgain ? eyeOff : eyeOn"
              @click.stop.prevent="showCurrentPasswordAgain"
            />
          </template>
        </InputStyle>
      </div>
      <div :class="style.buttons">
        <PrimaryButton
          text="Сохранить пароль"
          :disabled="!canChangePassword"
          @click="changePassword"
        />
      </div>
    </div>
    <div :class="[style.modal, style.modalDelete, style.modalSuccess]" v-else>
      <img src="$images/icons/badge/Name=Success,%20Size=L.svg" alt="" />
      <div :class="style.header">
        <h3>Пароль изменён</h3>
        <span>
          Новый пароль сохранён. Используйте его при следующем входе в аккаунт.
        </span>
      </div>
    </div>
  </ModalBlock>

  <LoaderComponent v-if="user === 'await'" />
  <div v-else-if="typeof user != 'string'" :class="style.mainBlock">
    <SectionHeaderBlock
      title="Аккаунт"
      subTitle="Управляйте информацией о своей учетной записи."
    >
      <template #buttons>
        <PrimaryButton
          text="Сохранить"
          :disabled="!canSaveStatus || isSaving"
          @click="saveUser"
        />
      </template>
    </SectionHeaderBlock>
    <form @submit.prevent :class="style.form">
      <div :class="style.inputs">
        <InputStyle
          description="Имя"
          size="M"
          placeholder="Ваше имя"
          errorText="Введите имя"
          positionError="top"
          v-model="user.name"
          :isError="error.name"
        />
        <InputStyle
          description="Телефон"
          size="M"
          type="tel"
          placeholder="+7 999 999-99-99"
          errorText="Укажите корректный номер телефона"
          positionError="top"
          v-model="user.phone"
          :isError="error.phone"
        />
        <InputStyle
          description="Email"
          size="M"
          type="email"
          placeholder="Ваш email"
          errorText="Укажите корректный email"
          positionError="top"
          v-model="user.email"
          :isError="error.email"
        />
      </div>
      <div :class="style.buttonsBottom">
        <SecondaryButton
          text="Сменить пароль"
          @click="openChangePasswordModal"
        />
        <GhostButtonError
          :icon="trashIcon"
          @click="openDeleteModal"
          text="Удалить аккаунт"
        />
      </div>
    </form>
  </div>
</template>
<script lang="ts">
import {API} from "@/api/generated";
import {defineComponent} from "vue";
import style from "./AccountComponent.module.scss";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import trashIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import eyeOn from "$images/icons/input/Name=Eye, Size=16, Tone=Default.svg";
import eyeOff from "$images/icons/input/Name=Eye-off, Size=16, Tone=Default.svg";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import ModalBlock from "@/layout/nav/components/modal/ModalBlock.vue";
import ToastError from "@/elements/UI/overlay/Toast/Error/ToastError.vue";
import ToastSuccess from "@/elements/UI/overlay/Toast/Success/ToastSuccess.vue";
import ActionModal from "@/layout/nav/components/modal/modals/ActionModal/ActionModal.vue";
import SectionHeaderBlock from "@/elements/UI/section/SectionHeaderBlock/SectionHeaderBlock.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";
import type {user as StoreUser} from "@/page/setting/account/userTypes";

type AccountUser = Pick<StoreUser, "name" | "email" | "phone">;

export default defineComponent({
  name: "AccountComponent",
  components: {
    LoaderComponent,
    GhostButtonError,
    ButtonIcon,
    SectionHeaderBlock,
    ActionModal,
    ToastSuccess,
    ToastError,
    ModalBlock,
    PrimaryButton,
    InputStyle,
    SecondaryButton
  },
  data() {
    return {
      style,

      trashIcon,
      eyeOn,
      eyeOff,
      error: {
        name: false,
        phone: false,
        email: false
      },
      passwords: {
        passwordOld: "",
        passwordNew: "",
        passwordNewCheck: ""
      },
      passwordsError: {
        passwordOldError: false,
        passwordNewError: false,
        passwordNewCheckError: false
      },
      passwordErrorText: "",
      user: "await" as string | AccountUser,
      originalUser: null as null | AccountUser,
      deleteModalVisible: false,
      changePasswordModalVisible: false,
      canSaveStatus: false,
      isSaving: false,
      isPasswordChanged: false,
      isShowPreviousPassword: false,
      isShowCurrentPassword: false,
      isShowCurrentPasswordAgain: false
    };
  },

  mounted() {
    window.addEventListener("keydown", this.handleSaveShortcut);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleSaveShortcut);
  },

  methods: {
    handleSaveShortcut(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyS") {
        event.preventDefault();

        if (this.deleteModalVisible || this.isSaving) {
          return;
        }

        if (this.changePasswordModalVisible && !this.isPasswordChanged) {
          if (this.canChangePassword) {
            void this.changePassword();
          }
          return;
        }

        if (this.canSaveStatus) {
          void this.saveUser();
        }
      }
    },

    async saveUser(): Promise<void> {
      if (!this.canSaveStatus || this.isSaving || typeof this.user === "string")
        return;

      if (!this.validateForm()) return;

      try {
        this.isSaving = true;

        this.user = await this.updateUser();

        this.originalUser = {...this.user};
        this.canSaveStatus = false;

        if (this.passwords.passwordNew !== "") {
          await this.changePassword();
        }

        const toastSuccess = this.$refs
          .ToastSaveSuccess as unknown as ToastType;
        toastSuccess.show();
      } catch {
        const toastError = this.$refs.ToastSaveError as unknown as ToastType;
        toastError.show();
      } finally {
        this.isSaving = false;
      }
    },
    openDeleteModal() {
      this.deleteModalVisible = true;
    },
    openChangePasswordModal() {
      this.changePasswordModalVisible = true;
    },
    async changePassword() {
      this.passwordsError = {
        passwordOldError: false,
        passwordNewError: false,
        passwordNewCheckError: false
      };

      const {passwordOld, passwordNew, passwordNewCheck} = this.passwords;

      const popup = this.$refs.ToastError as unknown as ToastType;

      if (!passwordOld) {
        this.passwordsError.passwordOldError = true;
        this.passwordErrorText = "Введите старый пароль";
        popup?.show();
        return;
      }

      if (passwordOld === passwordNew) {
        this.passwordsError.passwordNewError = true;
        this.passwordErrorText = "Придумайте новый пароль";
        popup?.show();
        return;
      }

      if (!passwordNew || passwordNew.length < 8) {
        this.passwordsError.passwordNewError = true;
        this.passwordErrorText = "Минимальная длина пароля 8 символов";
        popup?.show();
        return;
      }

      if (passwordNew !== passwordNewCheck) {
        this.passwordsError.passwordNewCheckError = true;
        this.passwordErrorText = "Повторите новый пароль";
        popup?.show();
        return;
      }

      try {
        const res = await API.updatePassword({
          body: {
            passwordOld,
            passwordNew
          }
        });

        if (res?._id) {
          this.passwords = {
            passwordOld: "",
            passwordNew: "",
            passwordNewCheck: ""
          };

          this.isPasswordChanged = true;
          setTimeout(() => {
            this.changePasswordModalVisible = false;
          }, 2000);
        }
      } catch (e) {
        const error = e as {data?: string[]; response?: {data?: string[]}};
        this.passwordsError.passwordOldError = true;
        this.passwordErrorText =
          error.response?.data?.[0] ??
          error.data?.[0] ??
          "Не удалось изменить пароль";
        console.error(e);
        popup?.show();
      }
    },
    async updateUser(): Promise<AccountUser> {
      if (typeof this.user === "string") {
        throw new Error("Invalid user obj");
      }
      const {name, email, phone} = await API.updateUser({
        body: {user: this.user}
      });

      if (typeof name !== "string") {
        throw new Error("Invalid user response");
      }

      return {name, email, phone};
    },
    validateForm() {
      this.error = {
        name: false,
        phone: false,
        email: false
      };

      if (typeof this.user === "string" || !this.user) return false;

      const {name, email, phone} = this.user;

      if (!name || name.trim().length < 2) {
        this.error.name = true;
      }

      const clearPhone = phone.replace(/\D/g, "");

      if (!clearPhone) {
        this.error.phone = true;
      } else if (clearPhone.length < 10 || clearPhone.length > 15) {
        this.error.phone = true;
      }

      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

      if (!email || !emailRegex.test(email)) {
        this.error.email = true;
      }

      return !Object.values(this.error).some((item) => item === true);
    },
    async deleteAccountHandler() {
      try {
        await API.deleteUser();
        await this.$router.push("/authorization");
      } catch {
        this.deleteModalVisible = false;

        const toastError = this.$refs.ToastSaveError as unknown as ToastType;
        toastError.show();
      }
    },
    showPreviousPassword() {
      this.isShowPreviousPassword = !this.isShowPreviousPassword;
    },

    showCurrentPassword() {
      this.isShowCurrentPassword = !this.isShowCurrentPassword;
    },

    showCurrentPasswordAgain() {
      this.isShowCurrentPasswordAgain = !this.isShowCurrentPasswordAgain;
    },

    resetPasswordVisibility() {
      this.isShowPreviousPassword = false;
      this.isShowCurrentPassword = false;
      this.isShowCurrentPasswordAgain = false;
    },

    resetPasswordForm() {
      this.passwords = {
        passwordOld: "",
        passwordNew: "",
        passwordNewCheck: ""
      };
      this.passwordsError = {
        passwordOldError: false,
        passwordNewError: false,
        passwordNewCheckError: false
      };
      this.passwordErrorText = "";
    },

    setAccountUser(data: StoreUser) {
      const {name, email, phone} = data;

      this.user = {name, email, phone};
      this.originalUser = {...this.user};
      this.canSaveStatus = false;
    }
  },
  computed: {
    getUser() {
      return this.$userStore.getUser;
    },
    canChangePassword(): boolean {
      return Boolean(
        this.passwords.passwordOld &&
        this.passwords.passwordNew &&
        this.passwords.passwordNewCheck
      );
    },
    buttonModalDelete(): ListActionItem[] {
      return [
        {
          text: "Удалить",
          handler: this.deleteAccountHandler
        },
        {
          text: "Отмена",
          handler: () => (this.deleteModalVisible = false)
        }
      ];
    }
  },
  watch: {
    getUser: {
      immediate: true,
      handler(data: StoreUser) {
        if (!data?._id) return;

        this.setAccountUser(data);
      }
    },
    passwords: {
      deep: true,
      handler() {
        this.passwordsError = {
          passwordOldError: false,
          passwordNewError: false,
          passwordNewCheckError: false
        };
      }
    },
    user: {
      deep: true,
      handler() {
        if (!this.originalUser || typeof this.user === "string") return;

        const current = JSON.stringify(this.user);
        const original = JSON.stringify(this.originalUser);

        this.canSaveStatus = current !== original;
      }
    },
    changePasswordModalVisible(value) {
      this.isPasswordChanged = false;

      if (!value) {
        this.resetPasswordForm();
        this.resetPasswordVisibility();
      }
    }
  }
});
</script>
